import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

@customElement("a2k-autocomplete")
export class A2kAutocomplete extends LitElement {
  @property({ attribute: false }) value = "";
  @property({ attribute: false }) options: string[] = [];
  @property() placeholder = "";
  @property() ariaLabel = "";
  @state() private open = false;
  @state() private active = -1;
  static styles = css`
    :host {
      display: block;
      position: relative;
      min-width: 0;
    }
    .input {
      box-sizing: border-box;
      width: 100%;
      min-height: 28px;
      padding: 3px 6px;
      border: 2px inset #fff;
      background: #fff;
      font: inherit;
    }
    .menu {
      position: absolute;
      z-index: 30;
      top: calc(100% + 2px);
      left: 0;
      right: 0;
      max-height: 220px;
      overflow-y: auto;
      padding: 2px;
      border: 2px outset #fff;
      background: #c0c0c0;
      box-shadow: 2px 2px #000;
    }
    .option {
      display: block;
      width: 100%;
      box-sizing: border-box;
      padding: 5px 7px;
      border: 0;
      background: #fff;
      color: #000;
      text-align: left;
      font: inherit;
      cursor: pointer;
    }
    .option:hover,
    .option[data-active="true"] {
      background: #000080;
      color: #fff;
    }
  `;
  private emit(value: string) {
    this.value = value;
    this.dispatchEvent(
      new CustomEvent("value-change", {
        detail: value,
        bubbles: true,
        composed: true,
      }),
    );
  }
  private filtered() {
    const query = this.value.trim().toLocaleLowerCase();
    return [...new Set(this.options)]
      .filter((item) => !query || item.toLocaleLowerCase().includes(query))
      .slice(0, 20);
  }
  private choose(value: string) {
    this.emit(value);
    this.open = false;
    this.active = -1;
  }
  private keydown(event: KeyboardEvent) {
    const values = this.filtered();
    if (event.key === "ArrowDown" && values.length) {
      event.preventDefault();
      this.open = true;
      this.active = (this.active + 1) % values.length;
    } else if (event.key === "ArrowUp" && values.length) {
      event.preventDefault();
      this.open = true;
      this.active = (this.active - 1 + values.length) % values.length;
    } else if (
      event.key === "Enter" &&
      this.open &&
      this.active >= 0 &&
      this.active < values.length
    ) {
      event.preventDefault();
      this.choose(values[this.active]);
    } else if (event.key === "Escape") {
      this.open = false;
      this.active = -1;
    }
  }
  render() {
    const values = this.filtered();
    return html`<input
        class="input"
        type="text"
        autocomplete="off"
        role="combobox"
        aria-autocomplete="list"
        aria-controls="suggestion-options"
        aria-activedescendant=${this.open && this.active >= 0 && this.active < values.length ? `suggestion-${this.active}` : nothing}
        aria-expanded=${String(this.open && values.length > 0)}
        aria-label=${this.ariaLabel || nothing}
        placeholder=${this.placeholder}
        .value=${this.value}
        @focus=${() => (this.open = true)}
        @blur=${() => window.setTimeout(() => (this.open = false), 120)}
        @keydown=${this.keydown}
        @input=${(event: Event) => {
          this.emit((event.target as HTMLInputElement).value);
          this.open = true;
          this.active = -1;
        }}
      />${
        this.open && values.length
          ? html`<div id="suggestion-options" class="menu" role="listbox">
              ${values.map(
                (value, index) =>
                  html`<button
                    class="option"
                    type="button"
                    role="option"
                    id=${`suggestion-${index}`}
                    aria-selected=${String(index === this.active)}
                    data-active=${String(index === this.active)}
                    @mousedown=${(event: Event) => event.preventDefault()}
                    @click=${() => this.choose(value)}
                  >
                    ${value}
                  </button>`,
              )}
            </div>`
          : nothing
      }`;
  }
}
